import React, { useState } from 'react';
import { PlayIcon, ArrowUpRightIcon } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { articles, episodes, founder, salon } from '../data/content';
const filters = ['Alles', 'Producten', 'Huidkennis', 'Energetisch werk'] as const;
type Filter = (typeof filters)[number];
export function Journal() {
  const [filter, setFilter] = useState<Filter>('Alles');
  const visible = filter === 'Alles' ? articles : articles.filter(a => a.category === filter);
  const [featured, ...rest] = visible;
  return <>
      {/* Hero */}
      <section className="bg-sand" data-id="element-469" data-mp="1t18af95919jm">
        <div className="mx-auto grid max-w-content gap-10 px-6 pb-12 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-16 lg:pt-14" data-id="element-470" data-mp="1xy8hnndu4icb">
          <div className="lg:col-span-8" data-id="element-471" data-mp="221mxoe1rwypr">
            <p className="rule-label text-[11px] uppercase tracking-widest text-clay" data-id="element-472" data-mp="39dx6kkanfrfr">
              Blog &amp; podcast
            </p>
            <h1 className="mt-7 max-w-3xl font-display text-[2.7rem] font-light leading-[1.02] text-ink sm:text-6xl" data-id="element-473" data-mp="3de78nlvblxpc">
              Hier deel ik mijn tips
              <span className="italic text-clay" data-id="element-474" data-mp="2td74hxg01qko"> en bevindingen.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 lg:self-end" data-id="element-475" data-mp="2odu8on75h95y">
            <div className="flex items-center gap-4 border-t border-line pt-6" data-id="element-476" data-mp="2ocj62qpfy7gf">
              <img src={founder.portrait} alt={founder.name} className="h-14 w-14 rounded-full object-cover object-top" data-id="element-477" data-mp="2kbh6u9vixveg" />
              <p className="text-[13px] leading-snug text-muted" data-id="element-478" data-mp="2amfo4bxlgzws">
                Geschreven en opgenomen door{' '}
                <span className="text-ink" data-id="element-479" data-mp="25oe7dxnn5coh">{founder.name}</span>
                <br data-id="element-480" data-mp="20ot8fn8h14br" />
                {founder.role} · {salon.city}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Podcast */}
      <section className="border-y border-line bg-ink py-16 text-shell lg:py-20" data-id="element-481" data-mp="1wp0tmsshi1xy">
        <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-482" data-mp="1rod3safzrr5b">
          <div className="lg:col-span-5" data-id="element-483" data-mp="1hyxu3pd1jrna">
            <p className="text-[11px] uppercase tracking-widest text-rose" data-id="element-484" data-mp="1m1ksya84qctz">De podcast</p>
            <h2 className="mt-5 font-display text-4xl font-light leading-[1.08] sm:text-[2.9rem]" data-id="element-485" data-mp="1821l7ij8d3i7">
              Elke twee weken een gesprek over huid en bewust leven
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-shell/70" data-id="element-486" data-mp="1d2zcckm7x7vu">
              Beschikbaar op Spotify, Apple Podcasts en overal waar je luistert.
            </p>
          </div>
          <ul className="divide-y divide-shell/15 border-y border-shell/15 lg:col-span-7" data-id="element-487" data-mp="25rwffghuyy40">
            {episodes.map(e => <li key={e.number} data-id="element-488" data-mp="1vzqswmadl8w7">
                <button type="button" className="group grid w-full gap-3 py-6 text-left sm:grid-cols-12 sm:items-center sm:gap-6" data-id="element-489" data-mp="1vypfib5w0abb">
                  <span className="flex items-center gap-4 sm:col-span-5" data-id="element-490" data-mp="2ogl32a7q7hjh">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-shell/30 transition-colors duration-150 ease-soft group-hover:bg-shell group-hover:text-ink" data-id="element-491" data-mp="d12mxqdkokck">
                      <PlayIcon className="h-3 w-3 fill-current" strokeWidth={1.5} data-id="element-492" data-mp="lu5ytu8kkp53" />
                    </span>
                    <span data-id="element-493" data-mp="ht3u4xvvvkvq">
                      <span className="block font-display text-xl font-light leading-snug" data-id="element-494" data-mp="hskks5kau4tb">
                        {e.title}
                      </span>
                      <span className="mt-0.5 block text-[12px] text-shell/60" data-id="element-495" data-mp="3l9awn12kbc2x">
                        Afl. {e.number} · {e.duration}
                      </span>
                    </span>
                  </span>
                  <span className="text-[14px] leading-relaxed text-shell/70 sm:col-span-7" data-id="element-496" data-mp="3u8ehoalai1k2">
                    {e.summary}
                  </span>
                </button>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Articles */}
      <section className="bg-sand py-16 lg:py-24" data-id="element-497" data-mp="24tn7yp1u718g">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-498" data-mp="24uydataxkh1o">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-499" data-mp="29rorfb36d2kg">
            <h2 className="font-display text-2xl font-light text-ink" data-id="element-500" data-mp="2erunr4ke3d8v">Recent geschreven</h2>
            <div className="flex flex-wrap gap-2" data-id="element-501" data-mp="2jqo1my3l969k">
              {filters.map(f => <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)} className={`rounded-full border px-4 py-1.5 text-[12px] transition-colors duration-150 ease-soft ${filter === f ? 'border-ink bg-ink text-shell' : 'border-line text-muted hover:border-ink hover:text-ink'}`} data-id="element-502" data-mp="2jobco0zexvn0">
                  {f}
                </button>)}
            </div>
          </div>

          {featured && <Reveal data-id="element-503" data-mp="zv041bx1bq5v">
              <article className="mt-10 grid gap-8 border-t border-ink/70 pt-10 lg:grid-cols-12 lg:gap-14" data-id="element-504" data-mp="zvjlysmoqejw">
                {featured.image ? <div className="lg:col-span-5" data-id="element-505" data-mp="vv9hxligdy7o">
                    <img src={featured.image} alt={featured.title} className="aspect-[4/3] w-full object-cover" data-id="element-506" data-mp="quv8q0548i82" />
                  </div> : null}
                <div className={featured.image ? 'lg:col-span-7' : 'lg:col-span-9'} data-id="element-507" data-mp="1jkajqpws6b3z">
                  <p className="text-[11px] uppercase tracking-widest text-clay" data-id="element-508" data-mp="1ek6a9juaign9">
                    {featured.category} · {featured.readTime} · {featured.date}
                  </p>
                  <h3 className="mt-4 max-w-2xl font-display text-3xl font-light leading-[1.1] text-ink sm:text-[2.6rem]" data-id="element-509" data-mp="14t1yeszwvbwn">
                    {featured.title}
                  </h3>
                  <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-muted" data-id="element-510" data-mp="1xh7iddj54wvf">
                    {featured.excerpt}
                  </p>
                  <p className="mt-6 text-[13px] text-muted" data-id="element-511" data-mp="3rsgtmxzccvlh">door {founder.name}</p>
                  <a href="#artikel" className="mt-6 inline-flex items-center gap-2 border-b border-ink/30 pb-1 text-sm text-ink transition-colors duration-150 ease-soft hover:border-ink" data-id="element-512" data-mp="3vtsk15q1hkuq">
                    Lees meer
                    <ArrowUpRightIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-513" data-mp="380k5li4vrr43" />
                  </a>
                </div>
              </article>
            </Reveal>}

          <ul className="mt-14 divide-y divide-line border-y border-line" data-id="element-514" data-mp="2z2765wm4kdmy">
            {rest.map((a, i) => <li key={a.slug} data-id="element-515" data-mp="341bc8edr5v92" data-mpi={i}>
                <Reveal delay={Math.min(i, 3) * 0.04} data-id="element-516" data-mp="342mh6hixp91r">
                  <a href="#artikel" className="group grid gap-3 py-7 sm:grid-cols-12 sm:items-baseline sm:gap-8" data-id="element-517" data-mp="xb3sszkuokyp">
                    <p className="text-[11px] uppercase tracking-widest text-clay sm:col-span-2" data-id="element-518" data-mp="1ccu35n0kt3x7">
                      {a.category}
                    </p>
                    <h3 className="font-display text-2xl font-light leading-snug text-ink transition-colors duration-150 ease-soft group-hover:text-clay sm:col-span-4" data-id="element-519" data-mp="17ftwpaztdz5k">
                      {a.title}
                    </h3>
                    <p className="text-[14px] leading-relaxed text-muted sm:col-span-4" data-id="element-520" data-mp="1v3layk4x0m9h">
                      {a.excerpt}
                    </p>
                    <p className="text-[12px] text-muted sm:col-span-2 sm:text-right" data-id="element-521" data-mp="1q3p5eggkkmrt">
                      {a.date} · {a.readTime}
                    </p>
                  </a>
                </Reveal>
              </li>)}
          </ul>

          {!featured && <p className="mt-10 border-t border-line pt-10 text-sm text-muted" data-id="element-522" data-mp="9ppbbm4enrch">
              Binnenkort meer in deze categorie.
            </p>}
        </div>
      </section>
    </>;
}